import React from "react";
import PropTypes from "prop-types";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

// ─── Font registration (mirrors RoofDetailReport.jsx pattern) ────────────────
import TitilliumWebBlack from "components/reports/fonts/TitilliumWeb-Black.ttf";
import TitilliumWebBold from "components/reports/fonts/TitilliumWeb-Bold.ttf";
import TitilliumWebBoldItalic from "components/reports/fonts/TitilliumWeb-BoldItalic.ttf";
import TitilliumWebExtraLight from "components/reports/fonts/TitilliumWeb-ExtraLight.ttf";
import TitilliumWebExtraLightItalic from "components/reports/fonts/TitilliumWeb-ExtraLightItalic.ttf";
import TitilliumWebItalic from "components/reports/fonts/TitilliumWeb-Italic.ttf";
import TitilliumWebLight from "components/reports/fonts/TitilliumWeb-Light.ttf";
import TitilliumWebLightItalic from "components/reports/fonts/TitilliumWeb-LightItalic.ttf";
import TitilliumWebRegular from "components/reports/fonts/TitilliumWeb-Regular.ttf";
import TitilliumWebSemiBold from "components/reports/fonts/TitilliumWeb-SemiBold.ttf";
import TitilliumWebSemiBoldItalic from "components/reports/fonts/TitilliumWeb-SemiBoldItalic.ttf";

// AZURE_IMAGE_URL / AZURE_IMAGE_URL_CROPED removed: URLs now come from item.photo.thumbnail_url / file_url

Font.register({
  family: "Public Sans",
  fonts: [
    { src: TitilliumWebRegular, fontWeight: "normal" },
    { src: TitilliumWebSemiBold, fontWeight: "semibold" },
    { src: TitilliumWebSemiBoldItalic, fontWeight: "semibold", fontStyle: "italic" },
    { src: TitilliumWebBlack, fontWeight: "black" },
    { src: TitilliumWebBold, fontWeight: "bold" },
    { src: TitilliumWebBoldItalic, fontWeight: "bold", fontStyle: "italic" },
    { src: TitilliumWebExtraLight, fontWeight: "extralight" },
    { src: TitilliumWebExtraLightItalic, fontWeight: "extralight", fontStyle: "italic" },
    { src: TitilliumWebItalic, fontWeight: "italic" },
    { src: TitilliumWebLight, fontWeight: "light" },
    { src: TitilliumWebLightItalic, fontWeight: "light", fontStyle: "italic" },
  ],
});

// ─── Caption character limits (same as SectionContent.jsx) ───────────────────
// Limits are proportional to the caption column height available per row.
const CAPTION_LIMITS = { 2: 750, 3: 500, 4: 300 };
const DEFAULT_CAPTION_LIMIT = 400;

/**
 * Converts HTML caption to plain text for PDF rendering.
 * @react-pdf/renderer 1.6.x does not support HTML — we strip tags here.
 * Lists are preserved as bullet/numbered prefixes.
 *
 * TODO: for Phase 4+, parse <strong>/<em>/<ul>/<ol> into react-pdf <Text> nodes
 *       with styles to preserve bold/italic/list formatting in the PDF.
 */
function htmlCaptionToPlainText(html) {
  if (!html) return "";
  // Replace list items with prefix before stripping
  let text = html
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/li>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<p[^>]*>/gi, "")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .trim();
  return text;
}

function truncateCaption(html, limit) {
  const text = htmlCaptionToPlainText(html);
  if (!text) return "";
  if (text.length <= limit) return text;
  return text.substring(0, limit) + "…";
}

function formatDateSimple(dateString) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return dateString;
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Returns the thumbnail URL for a report item.
 * Prefers item.photo.thumbnail_url (provided by getReport).
 * Returns null for orphan items (no nested photo object).
 *
 * NOTE: @react-pdf/renderer Image requires a non-null src; callers must guard
 * against null before rendering <Image>.
 */
function buildThumbnailUrl(projectId, item) {
  return item.photo?.thumbnail_url || null;
}

/**
 * Returns the full-resolution URL for a report item.
 * Used as fallback when the thumbnail is unavailable.
 * Returns null for orphan items.
 */
function buildFallbackUrl(projectId, item) {
  return item.photo?.file_url || null;
}

function parseTags(rawTags) {
  if (!rawTags) return null;
  if (typeof rawTags === "string") {
    try {
      const parsed = JSON.parse(rawTags);
      if (Array.isArray(parsed)) return parsed.join(", ");
      return parsed;
    } catch {
      return rawTags;
    }
  }
  if (Array.isArray(rawTags)) return rawTags.join(", ");
  return String(rawTags);
}

/**
 * resolveTokens — replaces {{token}} placeholders in a header/footer string.
 *
 * Supported tokens:
 *   {{report_title}}   → report.title
 *   {{report_date}}    → formatted created_at
 *   {{company_name}}   → report.company_name (if present)
 *   {{page_number}}    → literal "(page)" — actual page numbers use react-pdf render prop
 *
 * @param {string} text
 * @param {{ reportTitle: string, reportDate: string, companyName: string }} context
 * @returns {string}
 */
function resolveTokens(text, context) {
  if (!text) return "";
  return text
    .replace(/\{\{report_title\}\}/gi, context.reportTitle || "")
    .replace(/\{\{report_date\}\}/gi, context.reportDate || "")
    .replace(/\{\{company_name\}\}/gi, context.companyName || "")
    .replace(/\{\{page_number\}\}/gi, ""); // page numbers use react-pdf fixed render prop
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  // ── Shared ──
  page: {
    fontFamily: "Public Sans",
    fontSize: 10,
    color: "#2F4F4F",
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 30,
  },

  // ── Footer (absolute, fixed) ──
  footerContainer: {
    width: "100%",
    position: "absolute",
    bottom: 0,
  },
  footerPane: {
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 15,
    paddingRight: 15,
    borderTop: "1pt solid #98ABB6",
    marginLeft: 15,
    marginRight: 15,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  footerLegal: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 4,
  },
  footerBottom: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  footerText: {
    fontSize: 6,
    fontFamily: "Public Sans",
    color: "#98ABB6",
    fontWeight: "semibold",
  },

  // ── Page header (pages 2+) ──
  pageHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: "1pt solid #e0e0e0",
  },
  pageHeaderText: {
    fontSize: 8,
    color: "#98ABB6",
    fontFamily: "Public Sans",
  },

  // ── Cover page ──
  coverPage: {
    fontFamily: "Public Sans",
    color: "#2F4F4F",
    paddingBottom: 50,
  },
  coverPhotoWrapper: {
    width: "100%",
    height: 320,
    backgroundColor: "#e8e8e8",
    marginBottom: 0,
  },
  coverPhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  coverBody: {
    paddingHorizontal: 40,
    paddingTop: 36,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  coverTitle: {
    fontFamily: "Public Sans",
    fontSize: 22,
    fontWeight: "bold",
    color: "#1a1a2e",
    textAlign: "center",
    marginBottom: 10,
  },
  coverSubtitle: {
    fontFamily: "Public Sans",
    fontSize: 13,
    color: "#4169E1",
    textAlign: "center",
    marginBottom: 20,
  },
  coverMeta: {
    fontSize: 10,
    color: "#2F4F4F",
    textAlign: "center",
    marginBottom: 5,
  },
  coverMetaLabel: {
    fontWeight: "bold",
    color: "#708090",
  },

  // ── Section title page ──
  sectionTitlePage: {
    fontFamily: "Public Sans",
    color: "#2F4F4F",
    paddingTop: 80,
    paddingBottom: 50,
    paddingHorizontal: 40,
  },
  sectionTitleText: {
    fontFamily: "Public Sans",
    fontSize: 20,
    fontWeight: "bold",
    color: "#1a1a2e",
    marginBottom: 20,
    paddingBottom: 10,
    borderBottom: "2pt solid #4169E1",
  },
  sectionSummaryText: {
    fontFamily: "Public Sans",
    fontSize: 11,
    color: "#2F4F4F",
    lineHeight: 1.6,
  },

  // ── Photo rows (1 row per photo item; N rows stacked per page) ──
  // Each row: [photo image LEFT ~40%] + [info panel RIGHT flex:1]
  // Row height grows/shrinks based on photosPerPage (controlled via inline styles in PhotoItem).
  photoRow: {
    flexDirection: "row",
    marginBottom: 10,
    width: "100%",
    // borderBottom gives a subtle separator between rows within a page
    borderBottom: "0.5pt solid #f0f0f0",
    paddingBottom: 10,
  },

  // Info panel: sits to the right of the image, takes remaining width
  photoInfo: {
    flex: 1,
    paddingLeft: 12,
    paddingRight: 4,
    paddingTop: 2,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  },
  photoNumber: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#4169E1",
    marginBottom: 5,
  },
  metaLabel: {
    fontWeight: "bold",
    fontSize: 8.5,
    color: "#708090",
  },
  metaValue: {
    fontSize: 8.5,
    color: "#2F4F4F",
  },
  metaRow: {
    marginBottom: 3,
    flexDirection: "row",
    flexWrap: "wrap",
  },
  captionLabel: {
    fontWeight: "bold",
    fontSize: 8.5,
    color: "#708090",
    marginTop: 4,
  },
  captionText: {
    fontSize: 8.5,
    color: "#2F4F4F",
    marginTop: 2,
    lineHeight: 1.45,
  },
  pageDivider: {
    borderTop: "1pt solid #e8e8e8",
    marginVertical: 6,
  },
});

// ─── Shared footer component ──────────────────────────────────────────────────
const PDFFooter = () => (
  <View fixed style={styles.footerContainer}>
    <View style={styles.footerPane}>
      <View style={styles.footerLegal}>
        <Text style={{ ...styles.footerText, textAlign: "center" }}>
          This document is provided under License by xscope Technologies to the requestor for their
          Internal Use Only subject to the terms and conditions previously agreed to by the requestor
          when they registered for use of xscope Technologies Services. It remains the property of
          xscope Technologies and may be reproduced and distributed only within the requestor's
          company.
        </Text>
      </View>
      <View style={styles.footerBottom}>
        <Text style={styles.footerText}>
          {`Copyright © ${new Date().getFullYear()} www.x-scope.net | All rights reserved.`}
        </Text>
        <Text
          style={styles.footerText}
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          fixed
        />
      </View>
    </View>
  </View>
);

// ─── Page header (pages 2+) ───────────────────────────────────────────────────
const PDFPageHeader = ({ left, center, right }) => {
  if (!left && !center && !right) return null;
  return (
    <View style={styles.pageHeader} fixed>
      <Text style={styles.pageHeaderText}>{left || ""}</Text>
      <Text style={styles.pageHeaderText}>{center || ""}</Text>
      <Text style={styles.pageHeaderText}>{right || ""}</Text>
    </View>
  );
};

// ─── Cover page ───────────────────────────────────────────────────────────────
/**
 * CoverPage respects cover_settings toggles.
 * Each field is only rendered when its corresponding toggle is ON (defaults to true).
 */
const CoverPage = ({ report, totalPhotoCount }) => {
  const projectId = report.projectId || report.project_id;
  const coverPhotoId = report.cover_photo_id;
  const cs = report.cover_settings || {};

  // Toggle defaults: show everything unless explicitly set to false
  const showCoverPhoto = cs.show_cover_photo !== false;
  const showCompanyLogo = cs.show_company_logo !== false;
  const showCompanyName = cs.show_company_name !== false;
  const showCreatorName = cs.show_creator_name !== false;
  const showPhotoCount = cs.show_photo_count !== false;
  const showDateCreated = cs.show_date_created !== false;

  // Resolve cover photo URL from the nested photo object on the matching item.
  // Do NOT construct from coverPhotoId — it is now a BIGINT FK, not the Azure blob name.
  let coverPhotoUrl = null;
  if (showCoverPhoto && coverPhotoId) {
    const allItems = (report.sections || []).flatMap((s) => s.items || []);
    const coverItem = allItems.find(
      (it) => String(it.photo_id) === String(coverPhotoId)
    );
    if (coverItem?.photo?.thumbnail_url) {
      coverPhotoUrl = coverItem.photo.thumbnail_url;
    } else if (coverItem?.photo?.file_url) {
      // Thumbnail not available; use full-res as last resort
      coverPhotoUrl = coverItem.photo.file_url;
    }
    // If coverItem not found or photo is orphan → coverPhotoUrl stays null → grey placeholder
  }

  const subtitle = report.subtitle || formatDateSimple(report.created_at || report.createdAt);
  const creatorName = report.creator_name || report.creatorName || "";
  // Prefer the new nested company object; fall back to legacy flat fields for cached responses
  const companyLogoUrl = report.company?.logo_url || null;
  const companyName = report.company?.name || report.company_name || report.companyName || "";
  const dateCreated = formatDateSimple(report.created_at || report.createdAt);

  return (
    <Page size="A4" style={styles.coverPage}>
      {/* Cover photo */}
      {showCoverPhoto && (
        coverPhotoUrl ? (
          <View style={styles.coverPhotoWrapper}>
            <Image style={styles.coverPhoto} src={coverPhotoUrl} />
          </View>
        ) : (
          <View style={[styles.coverPhotoWrapper, { backgroundColor: "#c8d8e8" }]} />
        )
      )}

      {/* Cover body */}
      <View style={styles.coverBody}>
        <Text style={styles.coverTitle}>{report.title || "Report"}</Text>
        {subtitle ? <Text style={styles.coverSubtitle}>{subtitle}</Text> : null}

        {showCompanyLogo && companyLogoUrl ? (
          <Image
            style={{ width: 120, height: 60, objectFit: "contain", marginBottom: 10 }}
            src={companyLogoUrl}
          />
        ) : null}

        {showCompanyName && companyName ? (
          <Text style={styles.coverMeta}>
            <Text style={styles.coverMetaLabel}>Company: </Text>
            {companyName}
          </Text>
        ) : null}

        {showCreatorName && creatorName ? (
          <Text style={styles.coverMeta}>
            <Text style={styles.coverMetaLabel}>Created by: </Text>
            {creatorName}
          </Text>
        ) : null}

        {showPhotoCount ? (
          <Text style={styles.coverMeta}>
            <Text style={styles.coverMetaLabel}>Photos: </Text>
            {totalPhotoCount}
          </Text>
        ) : null}

        {showDateCreated && dateCreated ? (
          <Text style={styles.coverMeta}>
            <Text style={styles.coverMetaLabel}>Date: </Text>
            {dateCreated}
          </Text>
        ) : null}
      </View>

      <PDFFooter />
    </Page>
  );
};

// ─── Section title page ───────────────────────────────────────────────────────
/**
 * Emits a dedicated full page for sections WITHOUT items (so they are not
 * invisible in the PDF), or when show_section_title_pages is explicitly true.
 * For sections WITH items the title + summary render inline inside
 * SectionPhotosPages, so no separate page is needed.
 */
const SectionTitlePage = ({ section, settings, tokenContext }) => {
  const hasItems = (section.items || []).length > 0;

  // Sections WITH items never get a dedicated title page — title + summary
  // are rendered inline at the top of the first photo page by SectionPhotosPages.
  // A separate page would leave a mostly-empty page before the photos.
  if (hasItems) return null;

  // Sections WITHOUT items: render a title page only if there is something to show.
  if (!section.summary && !section.title) return null;

  const headerLeft = resolveTokens(settings && settings.header_left ? settings.header_left : "", tokenContext);
  const headerCenter = resolveTokens(settings && settings.header_center ? settings.header_center : "", tokenContext);
  const headerRight = resolveTokens(settings && settings.header_right ? settings.header_right : "", tokenContext);

  return (
    <Page size="A4" style={styles.sectionTitlePage}>
      <PDFPageHeader left={headerLeft} center={headerCenter} right={headerRight} />
      <Text style={styles.sectionTitleText}>{section.title || "Untitled Section"}</Text>
      {section.summary ? <Text style={styles.sectionSummaryText}>{section.summary}</Text> : null}
      <PDFFooter />
    </Page>
  );
};

// ─── Photo item within a page row ─────────────────────────────────────────────
const PhotoItem = ({ item, globalIndex, projectId, captionLimit, photosPerPage, settings }) => {
  // ── Row height budget (A4 = 841.89pt) ──────────────────────────────────────
  // Usable height after page padding (top:40, bottom:50) and fixed elements:
  //   - Page header (fixed): ~30pt
  //   - Section label (first page only, ~26pt) — we absorb this into the last row gap
  //   - Footer (fixed, absolute): already in paddingBottom
  //   - Row gap (marginBottom + paddingBottom = 20pt per row)
  // Remaining ≈ 841 - 40 - 50 - 30 = 721pt shared across N rows + (N × 20pt gaps)
  // Row content height = (721 - N × 20) / N
  //   2 → (721 - 40) / 2  = 340.5 → image height ~190pt  (keeps landscape feel)
  //   3 → (721 - 60) / 3  = 220.3 → image height ~135pt
  //   4 → (721 - 80) / 4  = 160.25 → image height ~105pt
  //
  // Image width is fixed at 38% of the page content width (~535pt × 0.38 ≈ 203pt).
  // The info panel fills the remaining 62%.
  const imageWidthMap = { 2: "38%", 3: "38%", 4: "38%" };
  const imageHeightMap = { 2: 190, 3: 135, 4: 105 };
  const imgWidth = imageWidthMap[photosPerPage] || "38%";
  const imgHeight = imageHeightMap[photosPerPage] || 135;

  // Use item.photo.thumbnail_url (getReport response); fallback to file_url.
  // Both buildThumbnailUrl and buildFallbackUrl return null for orphan items.
  const thumbnailUrl = buildThumbnailUrl(projectId, item) || buildFallbackUrl(projectId, item);

  // Metadata: prefer item.photo nested fields (new API); fall back to flat fields (old API)
  const metaTagsRaw = item.photo?.tags ?? item.tags;
  const metaCreateTime = item.photo?.create_time ?? item.create_time ?? item.created_at ?? item.created;
  const metaUserName = item.photo?.user_name ?? item.user_name;
  const metaProjectName = item.photo?.project_name ?? item.project_name;

  const tagsLabel = parseTags(metaTagsRaw);
  const caption = truncateCaption(item.caption || "", captionLimit);
  const dateLabel = formatDateSimple(metaCreateTime);

  // settings toggles (default true)
  const showPhotoNumbers = !settings || settings.show_photo_numbers !== false;
  const showCapturedBy = !settings || settings.show_photo_captured_by !== false;
  const showLocation = !settings || settings.show_location_captured !== false;
  const showDate = !settings || settings.show_date_captured !== false;
  const showTags = !settings || settings.show_photo_tags !== false;

  return (
    <View style={styles.photoRow} wrap={false}>
      {/*
        Image block — fixed width + height forms the left column of each row.
        objectFit: "contain" preserves native aspect ratio without stretching.
        A light grey background fills the letterbox areas when the photo is not 4:3.
        For orphan items (null URL) we render a placeholder view.
      */}
      <View
        style={{
          width: imgWidth,
          height: imgHeight,
          backgroundColor: "#e8e8e8",
          flexShrink: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {thumbnailUrl ? (
          <Image
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            src={thumbnailUrl}
          />
        ) : (
          // Orphan placeholder — grey block with no broken-image icon
          <View style={{ width: "100%", height: "100%", backgroundColor: "#d0d0d0" }} />
        )}
      </View>

      {/* Info panel */}
      <View style={styles.photoInfo}>
        {showPhotoNumbers && (
          <Text style={styles.photoNumber}>#{globalIndex}</Text>
        )}

        {showLocation && metaProjectName ? (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Project: </Text>
            <Text style={styles.metaValue}>{metaProjectName}</Text>
          </View>
        ) : null}

        {showDate && dateLabel ? (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Date: </Text>
            <Text style={styles.metaValue}>{dateLabel}</Text>
          </View>
        ) : null}

        {showCapturedBy && metaUserName ? (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Creator: </Text>
            <Text style={styles.metaValue}>{metaUserName}</Text>
          </View>
        ) : null}

        {showTags && tagsLabel ? (
          <View style={styles.metaRow}>
            <Text style={styles.metaLabel}>Tags: </Text>
            <Text style={styles.metaValue}>{tagsLabel}</Text>
          </View>
        ) : null}

        {caption ? (
          <View style={{ marginTop: 4 }}>
            <Text style={styles.captionLabel}>Caption:</Text>
            <Text style={styles.captionText}>{caption}</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ─── Photos pages for one section ─────────────────────────────────────────────
const SectionPhotosPages = ({
  section,
  report,
  startIndex,
  photosPerPage,
  captionLimit,
  settings,
  tokenContext,
  showSummaryInline,
}) => {
  const projectId = report.projectId || report.project_id;
  const items = [...(section.items || [])].sort(
    (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
  );

  if (items.length === 0) return null;

  // Group items into pages
  const pages = [];
  for (let i = 0; i < items.length; i += photosPerPage) {
    pages.push(items.slice(i, i + photosPerPage));
  }

  const headerLeft = resolveTokens(settings && settings.header_left ? settings.header_left : "", tokenContext);
  const headerCenter = resolveTokens(settings && settings.header_center ? settings.header_center : "", tokenContext);
  const headerRight = resolveTokens(settings && settings.header_right ? settings.header_right : "", tokenContext);

  return pages.map((pageItems, pageIdx) => {
    const baseGlobal = startIndex + pageIdx * photosPerPage;

    return (
      <Page key={pageIdx} size="A4" style={styles.page}>
        <PDFPageHeader left={headerLeft} center={headerCenter} right={headerRight} />

        {/* Section label (and optional inline summary) on first page of each section */}
        {pageIdx === 0 && (
          <View style={{ marginBottom: 10 }}>
            <Text
              style={{
                fontFamily: "Public Sans",
                fontSize: 13,
                fontWeight: "bold",
                color: "#4169E1",
                marginBottom: 2,
              }}
            >
              {section.title || "Untitled Section"}
            </Text>
            {showSummaryInline && section.summary ? (
              <Text
                style={{
                  fontFamily: "Public Sans",
                  fontSize: 10,
                  fontWeight: "normal",
                  color: "#555555",
                  marginBottom: 4,
                }}
              >
                {section.summary}
              </Text>
            ) : null}
            <View style={styles.pageDivider} />
          </View>
        )}

        {pageItems.map((item, itemIdx) => (
          <PhotoItem
            key={item.id}
            item={item}
            globalIndex={baseGlobal + itemIdx + 1}
            projectId={projectId}
            captionLimit={captionLimit}
            photosPerPage={photosPerPage}
            settings={settings}
          />
        ))}

        <PDFFooter />
      </Page>
    );
  });
};

// ─── Main Document ────────────────────────────────────────────────────────────
/**
 * ReportPDF — @react-pdf/renderer Document for the new Reports feature.
 *
 * Respects:
 *   - cover_settings toggles (show_cover_photo, show_company_logo, etc.)
 *   - settings toggles (show_section_title_pages, show_photo_numbers, etc.)
 *   - settings header L/C/R with token resolution
 *
 * @param {object} report - Full report object from ProjectReportsService.getReport()
 */
const ReportPDF = ({ report }) => {
  if (!report) return null;

  const photosPerPage = report.photos_per_page || report.photosPerPage || 3;
  const captionLimit = CAPTION_LIMITS[photosPerPage] || DEFAULT_CAPTION_LIMIT;
  const settings = report.settings || {};
  const sections = (report.sections || []).filter((s) => (s.items || []).length > 0 || s.summary);

  // Total photo count across all sections (for cover page)
  const totalPhotoCount = (report.sections || []).reduce(
    (sum, s) => sum + (s.items || []).length,
    0
  );

  // Build cumulative start indices per section (for global photo numbering)
  const sectionStartIndices = [];
  let runningIndex = 0;
  (report.sections || []).forEach((sec) => {
    sectionStartIndices.push(runningIndex);
    runningIndex += (sec.items || []).length;
  });

  // Token context for header resolution
  const tokenContext = {
    reportTitle: report.title || "",
    reportDate: formatDateSimple(report.created_at || report.createdAt),
    companyName: report.company_name || report.companyName || "",
  };

  return (
    <Document>
      {/* Cover page — always present */}
      <CoverPage report={report} totalPhotoCount={totalPhotoCount} />

      {/* Sections */}
      {sections.map((section, secIdx) => {
        const sectionItems = (section.items || []).filter(Boolean);
        const startIndex = sectionStartIndices[secIdx] || 0;
        const hasItems = sectionItems.length > 0;
        // A dedicated title page is only emitted for sections WITHOUT items
        // (SectionTitlePage always returns null when hasItems is true).
        // Therefore the summary is always rendered inline on the first photo page.
        const titlePageWillRender = !hasItems && (section.summary || section.title);

        return (
          <React.Fragment key={section.id}>
            {/* Section title page — only for sections without items */}
            <SectionTitlePage
              section={section}
              settings={settings}
              tokenContext={tokenContext}
            />

            {/* Photo pages — summary always renders inline (title page never emits for sections with items) */}
            {hasItems && (
              <SectionPhotosPages
                section={section}
                report={report}
                startIndex={startIndex}
                photosPerPage={photosPerPage}
                captionLimit={captionLimit}
                settings={settings}
                tokenContext={tokenContext}
                showSummaryInline={!titlePageWillRender}
              />
            )}
          </React.Fragment>
        );
      })}
    </Document>
  );
};

ReportPDF.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    subtitle: PropTypes.string,
    cover_photo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    photos_per_page: PropTypes.number,
    photosPerPage: PropTypes.number,
    created_at: PropTypes.string,
    createdAt: PropTypes.string,
    creator_name: PropTypes.string,
    creatorName: PropTypes.string,
    company: PropTypes.shape({
      name: PropTypes.string,
      logo_url: PropTypes.string,
    }),
    company_name: PropTypes.string,
    companyName: PropTypes.string,
    cover_settings: PropTypes.object,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        summary: PropTypes.string,
        items: PropTypes.array,
      })
    ),
    settings: PropTypes.object,
  }),
};

export default ReportPDF;
